// src/pages/Achievements.tsx
// 目標成就 Memento 頁面 - 紀念物收藏體驗
//
// 設計理念：
// - 從「儀表板」轉變為「紀念物收藏」
// - 靈感來自票根和拍立得照片的情感價值
// - 月份分組營造時間積累的感覺
//
// 重構記錄 (2025-12-17):
// - Header 簡化：移除 Total/Avg Ripples，聚焦完成數量
// - 月份分組：使用 Dec '25 格式
// - 拍立得卡片：MementoCard 取代原有卡片
// - Journey Modal：點擊卡片查看旅程視覺化

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy } from 'lucide-react';
import { Objective } from '../features/goal/types/objective';
import ObjectiveService from '../features/goal/services/objectiveService';
import { logger } from '../utils/logger';
import { groupObjectivesByMonth } from '../features/goal/utils/objectiveUtils';
import { useHeader } from '../hooks/useHeader';
import { MementoCard, JourneyModal } from '../components/achievements';

/**
 * Achievements 頁面 - Memento 紀念物收藏
 *
 * 視覺結構：
 * - Hero 區域：聚焦完成的目標數量
 * - 月份分組：按時間降序排列（最新在上）
 * - 拍立得卡片：點擊開啟 Journey Modal
 */
const Achievements: React.FC = () => {
  const navigate = useNavigate();
  const [objectives, setObjectives] = useState<Objective[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal 狀態
  const [selectedObjective, setSelectedObjective] = useState<Objective | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // 配置 Header (Ceramic Theme Adaption)
  const headerConfig = useMemo(() => ({
    title: 'Achievements',
    className: 'text-gray-900', // Dark text for light theme
    leftAction: (
      <button
        onClick={() => navigate('/goals')}
        className="md:hidden p-2 -ml-2 rounded-lg hover:bg-gray-200 transition-colors text-gray-900"
        aria-label="Back to Goals"
      >
        <ArrowLeft size={20} className="text-gray-900" />
      </button>
    ),
  }), [navigate]);

  useHeader(headerConfig);

  // 載入所有完成的目標
  useEffect(() => {
    loadCompletedObjectives();
  }, []);

  const loadCompletedObjectives = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await ObjectiveService.getAllNonArchivedObjectives();
      const completedGoals = result.filter(obj => obj.status === 'completed');

      // 按完成時間排序（最新的在最前面）
      completedGoals.sort((a, b) => {
        if (!a.completedAt || !b.completedAt) return 0;
        return b.completedAt.seconds - a.completedAt.seconds;
      });

      setObjectives(completedGoals);
      logger.dev('Achievements: Loaded completed objectives', { count: completedGoals.length });

    } catch (err) {
      logger.error('Achievements: Failed to load objectives', err);
      setError(err instanceof Error ? err.message : 'Failed to load achievements');
    } finally {
      setLoading(false);
    }
  };

  // 計算統計資訊（簡化版：只需要總數）
  const stats = useMemo(() => ({
    totalGoals: objectives.length,
  }), [objectives]);

  // 按月份分組
  const groupedObjectives = useMemo(() =>
    groupObjectivesByMonth(objectives), [objectives]
  );

  // Modal 控制
  const openJourneyModal = (objective: Objective) => {
    setSelectedObjective(objective);
    setIsModalOpen(true);
  };

  const closeJourneyModal = () => {
    setIsModalOpen(false);
    // 延遲清除選中的目標，讓關閉動畫完成
    setTimeout(() => setSelectedObjective(null), 200);
  };

  // 渲染空狀態 (Ceramic/Light Mode)
  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 bg-white border border-gray-100 shadow-sm">
        <Trophy className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="text-lg font-semibold text-gray-900 mb-2">
        No Achievements Yet
      </h3>
      <p className="text-gray-500 text-sm mb-6 text-center max-w-sm">
        Complete your first goal to light up your Hall of Fame!
      </p>
      <button
        onClick={() => navigate('/goals')}
        className="btn-primary px-6 py-2.5 rounded-full font-semibold text-sm"
      >
        Start Achieving
      </button>
    </div>
  );

  return (
    // Background consistent with application theme
    <div className="min-h-screen bg-surface-page text-gray-900 overflow-x-hidden">
      <div className="content-standard pt-6 pb-20">
        {/* 錯誤提示 (Style adaptations needed for light mode, but keeping functional) */}
        {error && (
          <div className="px-4 py-3 bg-red-100 border border-red-200 text-red-700 text-sm rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading 狀態 */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
          </div>
        ) : objectives.length === 0 ? (
          renderEmptyState()
        ) : (
          <div className="space-y-12">
            {/* Hero 統計區域 - Digital Spectrum Style */}
            <section
              className="relative rounded-[24px] overflow-hidden group"
            >
              {/* Materiality: Curved Texture and Deepened Gray */}
              <div
                className="absolute inset-0 z-0"
                style={{
                  backgroundColor: '#E5E7EB', // Deepened Gray Base
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100%25' height='100%25' viewBox='0 0 1000 1000' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='100' height='100' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 100 0 L 0 0 0 100' fill='none' stroke='black' stroke-width='1'/%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100%25' height='100%25' fill='url(%23grid)' opacity='0'/%3E%3Cpath d='M0,500 C200,400 300,600 500,500 S800,400 1000,500' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,520 C220,420 320,620 520,520 S820,420 1000,520' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,540 C240,440 340,640 540,540 S840,440 1000,540' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,560 C260,460 360,660 560,560 S860,460 1000,560' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,580 C280,480 380,680 580,580 S880,480 1000,580' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,600 C300,500 400,700 600,600 S900,500 1000,600' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,620 C320,520 420,720 620,620 S920,520 1000,620' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,480 C180,380 280,580 480,480 S780,380 1000,480' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,460 C160,360 260,560 460,460 S760,360 1000,460' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,440 C140,340 240,540 440,440 S740,340 1000,440' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,420 C120,320 220,520 420,420 S720,320 1000,420' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,400 C100,300 200,500 400,400 S700,300 1000,400' fill='none' stroke='rgba(0,0,0,0.03)' stroke-width='2'/%3E%3Cpath d='M0,200 Q 250,50 500,200 T 1000,200' fill='none' stroke='rgba(0,0,0,0.015)' stroke-width='50'/%3E%3Cpath d='M0,800 Q 250,950 500,800 T 1000,800' fill='none' stroke='rgba(0,0,0,0.015)' stroke-width='50'/%3E%3C/svg%3E")`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }}
              ></div>

              {/* Top Aurora Glow (Digital Light Soul) */}
              <div
                className="absolute pointer-events-none z-0"
                style={{
                  top: '-50%',
                  left: '-50%',
                  width: '200%',
                  height: '200%',
                  background: 'radial-gradient(circle at 50% 0%, rgba(120, 119, 198, 0.1), transparent 40%)',
                }}
              ></div>

              {/* Content Container */}
              <div className="relative z-10 px-6 py-16 flex flex-col items-center justify-center text-center">
                {/* Trophy Icon - Top Left */}
                <div className="absolute top-6 left-6">
                  <div className="w-12 h-12 rounded-full bg-gray-900/5 flex items-center justify-center backdrop-blur-sm">
                    <Trophy className="w-6 h-6 text-gray-700" />
                  </div>
                </div>

                {/* Hero Number - Etching Effect */}
                <div className="relative mb-2">
                  <h1
                    className="relative text-8xl md:text-9xl font-bold tracking-tighter leading-none select-none"
                    style={{
                      fontFamily: '"SF Pro Rounded", ui-rounded, system-ui, sans-serif',
                      color: '#48484A', // Deep Granite Grey
                      textShadow: '0 1px 0 rgba(255,255,255, 1)' // Letterpress effect
                    }}
                  >
                    {stats.totalGoals}
                  </h1>
                </div>

                {/* Hero Label - Cinematic/Gallery style */}
                <div className="flex items-center gap-3 mb-1 opacity-80">
                  <div className="h-[1px] w-12 bg-gray-300"></div>
                  <span className="text-sm font-medium uppercase tracking-[0.3em] text-gray-500">
                    Goals Achieved
                  </span>
                  <div className="h-[1px] w-12 bg-gray-300"></div>
                </div>

                <p className="text-gray-400 text-[10px] tracking-widest font-medium mt-4 uppercase">
                  Hall of Fame
                </p>
              </div>
            </section>

            {/* 月份分組列表 */}
            {Array.from(groupedObjectives.entries()).map(([month, monthObjectives]) => (
              <section key={month} className="relative z-10">
                {/* 月份標題 */}
                <div className="flex items-center gap-4 mb-6">
                  <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                    {month}
                  </h2>
                  <div className="h-[1px] flex-1 bg-gray-200"></div>
                </div>

                {/* 拍立得卡片網格 */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
                  {monthObjectives.map((objective) => (
                    <MementoCard
                      key={objective.id}
                      objective={objective}
                      onClick={() => openJourneyModal(objective)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Journey Modal */}
        {selectedObjective && (
          <JourneyModal
            objective={selectedObjective}
            isOpen={isModalOpen}
            onClose={closeJourneyModal}
          />
        )}
      </div>
    </div>
  );
};

export default Achievements;
